"use client";

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { useFeatureFlags } from "../feature-flags/feature-flags-context";

/** Filters every report on the page shares. */
export interface ReportGlobalFilters {
  date_from?: string;
  date_to?: string;
  contact_id?: string;
}

/**
 * One cache slot. The payload is whatever report produced it, so it stays
 * `unknown` — a consumer narrows it against its own report's row type.
 */
export interface CachedReportEntry {
  data: unknown;
  timestamp: number;
}

/** A row an export helper can serialise (see `objectsToCSV`). */
export type ExportableRow = Record<string, unknown>;

interface ReportsContextType {
  // Shared filters
  globalFilters: ReportGlobalFilters;
  setGlobalFilters: (filters: Partial<ReportGlobalFilters>) => void;

  // Shared data
  cachedData: Record<string, CachedReportEntry>;
  setCachedData: (key: string, data: unknown) => void;
  clearCachedData: () => void;

  // Export functions
  exportToCSV: (data: ExportableRow[], filename: string) => void;
  exportToPDF: (data: ExportableRow[], filename: string) => void;
}

const ReportsContext = createContext<ReportsContextType | undefined>(undefined);

export const useReports = () => {
  const context = useContext(ReportsContext);
  if (!context) {
    throw new Error("useReports must be used within a ReportsProvider");
  }
  return context;
};

interface ReportsProviderProps {
  children: React.ReactNode;
}

export const ReportsProvider: React.FC<ReportsProviderProps> = ({ children }) => {
  const { appUser, isClientUser } = useUniversalAuth();
  const { flags } = useFeatureFlags();

  // The default 30-day window is filled in on the CLIENT. Reading the clock in
  // this initializer ran during render, and this provider is mounted in hub's
  // root layout — so under `cacheComponents` every page prerender failed on it.
  // Empty strings are the "no window chosen yet" state.
  const [globalFilters, setGlobalFiltersState] = useState<ReportGlobalFilters>({
    date_from: "",
    date_to: "",
    contact_id: undefined, // TODO: Implement contact_id from appUser
  });

  useEffect(() => {
    setGlobalFiltersState((prev) =>
      prev.date_from || prev.date_to
        ? prev
        : {
            ...prev,
            date_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
              .toISOString()
              .split("T")[0],
            date_to: new Date().toISOString().split("T")[0],
          }
    );
  }, []);

  const [cachedData, setCachedDataState] = useState<Record<string, CachedReportEntry>>({});

  const setGlobalFilters = useCallback((filters: Partial<ReportGlobalFilters>) => {
    setGlobalFiltersState((prev) => ({
      ...prev,
      ...filters,
    }));
  }, []);

  const setCachedData = useCallback((key: string, data: unknown) => {
    setCachedDataState((prev) => ({
      ...prev,
      [key]: {
        data,
        timestamp: Date.now(),
      },
    }));
  }, []);

  const clearCachedData = useCallback(() => {
    setCachedDataState({});
  }, []);

  const exportToCSV = useCallback(
    (data: ExportableRow[], filename: string) => {
      if (!flags.canExportReports) return;

      // Import dynamically to avoid SSR issues
      void import("@/lib/utils/csv-export").then(({ objectsToCSV, downloadCSV }) => {
        const csvData = objectsToCSV(data, {
          filename,
          includeHeaders: true,
        });
        downloadCSV(csvData, filename);
      });
    },
    [flags.canExportReports]
  );

  const exportToPDF = useCallback(
    (data: ExportableRow[], filename: string) => {
      if (!flags.canExportReports) return;

      // PDF export implementation would go here
      console.log("PDF export not implemented yet", { data, filename });
    },
    [flags.canExportReports]
  );

  const value: ReportsContextType = useMemo(
    () => ({
      globalFilters,
      setGlobalFilters,
      cachedData,
      setCachedData,
      clearCachedData,
      exportToCSV,
      exportToPDF,
    }),
    [
      globalFilters,
      setGlobalFilters,
      cachedData,
      setCachedData,
      clearCachedData,
      exportToCSV,
      exportToPDF,
    ]
  );

  return <ReportsContext.Provider value={value}>{children}</ReportsContext.Provider>;
};
